import type { LinkField } from '@prismicio/client';

import type {
  LocationBannerAlertDocument,
  LocationBannerAlertDocumentDataLocationItem,
} from '@/types';
import { isCurrentDateInRange } from '@/utils';

export interface PromotionBannerMessage {
  active: boolean;
  cta: LinkField;
  dismissible: boolean;
  internalCta: string;
  message: string;
}

/** Slugs can be comma-separated; empty slug = all stores. Requires store when slug is set. */
export function promotionLocationMatchesStore(
  locationSlug: string | null | undefined,
  storeUrlSlug: string | undefined,
): boolean {
  if (!locationSlug?.trim()) return true;
  if (!storeUrlSlug) return false;
  const slugs = locationSlug
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  return slugs.includes(storeUrlSlug);
}

/** Day + date window only (store slug applied in resolvePromotionBannerMessages). */
export function filterPromotionRowsBySchedule(
  rows: LocationBannerAlertDocumentDataLocationItem[] | undefined | null,
  currentDay: string,
  timeZone?: string,
): LocationBannerAlertDocumentDataLocationItem[] {
  if (!rows?.length) return [];
  return rows.filter(
    row =>
      (!row?.day || row.day === 'Everyday' || row.day === currentDay) &&
      isCurrentDateInRange(
        row?.start_date as string,
        row?.end_date as string,
        timeZone,
      ),
  );
}

/**
 * After schedule filtering, resolves location vs global default.
 */
export function resolvePromotionBannerMessages(
  promotion: LocationBannerAlertDocument | undefined,
  scheduleFilteredRows: LocationBannerAlertDocumentDataLocationItem[],
  storeUrlSlug: string | undefined,
): { locationSlug: string; messages: PromotionBannerMessage[] } {
  const data = promotion?.data;
  if (!data) {
    return { locationSlug: '', messages: [] };
  }

  const forStore = scheduleFilteredRows.filter(row =>
    promotionLocationMatchesStore(row?.location_slug, storeUrlSlug),
  );

  if (forStore.some(row => row.active)) {
    return {
      locationSlug: storeUrlSlug ?? '',
      messages: forStore.map(row => ({
        active: !!row.active,
        cta: row.cta,
        dismissible: !!row.dismissible,
        internalCta: row.internal_cta ?? '',
        message: row.message ?? '',
      })),
    };
  }

  return {
    locationSlug: '',
    messages: [
      {
        active: !!data.default_active,
        cta: data.default_cta,
        dismissible: !!data.global_dismissible,
        internalCta: data.default_internal_cta ?? '',
        message: data.default_message ?? '',
      },
    ],
  };
}
